import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Chip, Stack } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const alerts = [
  {
    id: 1,
    type: 'critical',
    title: 'High-Risk Privilege Creep Detected',
    message: 'Multiple users flagged with excessive group memberships exceeding peer average by 200%.',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    tabIndex: 1,
  },
  {
    id: 2,
    type: 'warning',
    title: 'Excessive Access Anomalies',
    message: 'Users with access levels significantly higher than their job role peers detected.',
    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    tabIndex: 2,
  },
  {
    id: 3,
    type: 'info',
    title: 'Dormant Accounts with Active Access',
    message: 'Accounts inactive for 90+ days still have active privileged access requiring review.',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    tabIndex: 4,
  },
];

const typeConfig = {
  critical: { color: 'error', icon: ErrorOutlineIcon, borderColor: 'error.main' },
  warning: { color: 'warning', icon: WarningAmberIcon, borderColor: 'warning.main' },
  info: { color: 'info', icon: InfoOutlinedIcon, borderColor: 'info.main' },
};

function formatTimestamp(timestamp) {
  const diffMin = Math.floor((Date.now() - new Date(timestamp)) / 60000);
  if (diffMin < 60) return `${diffMin} minutes ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)} hours ago`;
  return new Date(timestamp).toLocaleDateString();
}

/* ── Shared sub-card style (matches risk metric cards) ─────────── */
const subCardSx = (borderColor) => ({
  p: 2,
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  borderLeft: 3,
  borderLeftColor: borderColor,
  bgcolor: 'background.subtle',
  cursor: 'pointer',
  transition: 'all 0.15s',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  '&:hover': {
    borderColor: 'rgba(99, 102, 241, 0.3)',
    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'action.hover',
  },
});

const AlertsList = () => {
  const navigate = useNavigate();

  return (
    <Grid2 container spacing={1.5} sx={{ flex: 1 }}>
      {alerts.map((alert) => {
        const cfg = typeConfig[alert.type] || typeConfig.info;
        const Icon = cfg.icon;
        return (
          <Grid2 key={alert.id} xs={12} sm={6}>
            <Box
              sx={subCardSx(cfg.borderColor)}
              onClick={() => navigate('/profile360/analytics', { state: { activeTab: alert.tabIndex } })}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Icon sx={{ fontSize: 16, color: `${cfg.color}.main` }} />
                <Chip
                  label={alert.type.toUpperCase()}
                  color={cfg.color}
                  size="small"
                  sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                />
                <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto !important', fontSize: '0.7rem' }}>
                  {formatTimestamp(alert.timestamp)}
                </Typography>
              </Stack>
              <Typography variant="subtitle2" sx={{ fontSize: '0.82rem', lineHeight: 1.3 }}>{alert.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                {alert.message}
              </Typography>
              <Typography variant="caption" color="primary.main" fontWeight={500} sx={{ mt: 'auto' }}>
                View Details &rarr;
              </Typography>
            </Box>
          </Grid2>
        );
      })}
    </Grid2>
  );
};

export default AlertsList;
